import { Plus, Trash } from "@/assets/icons";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Divider,
  Dropdown,
  DropdownOption,
  // Panel,
  Row,
  RowSize,
  ValidationWrapper,
} from "@/components";
import {
  DEFAULT_LOADING_TEXT,
  DEFAULT_SELECT_PLACEHOLDER_TEXT,
} from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { appConfig } from "@/guards/auth/azure-b2c.config";
import { useAreas } from "@/pages/requests/components/areas/use-areas";
import { RequestResponse } from "@/services/api";
import { selectRequestFormStore, useBoundStore } from "@/services/stores";
import { getBooleanConfigValue } from "@/utils";
import clsx from "clsx";
import { filter, find, map, size } from "lodash";
import {
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import styles from "./styles.scss";
import { AreasField, AreasFieldLabel } from "./types";

export const AreasForm: FC<{
  data?: RequestResponse | null;
}> = ({ data }) => {
  const { account } = useContext(AuthContext);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { requestFormData, setRequestFormData, removeRequestFormData } =
    useBoundStore(selectRequestFormStore);

  const { isLoading, areas } = useAreas(
    account?.username ?? "",
    requestFormData?.ContractType?.Id ?? 0,
    requestFormData?.ContractType?.CompanyId ?? 0,
  );

  const {
    clearErrors,
    getValues,
    setValue,
    setError,
    formState: { errors },
    trigger,
  } = useFormContext();

  if (getBooleanConfigValue(appConfig?.FEATURES?.IS_DEBUG_ENABLED)) {
    /* eslint-disable */
    console.log("AreasForm", errors);
    /* eslint-enable */
  }

  useEffect(() => {
    if (
      !size(requestFormData?.[AreasField.Areas]) ||
      !size(getValues(AreasField.Areas))
    ) {
      setError("init", { message: "AreasForm" });

      trigger(AreasField.Areas);
    }
  }, [getValues, requestFormData, setError, trigger]);

  const handleDataChange = useCallback(
    (value: DropdownOption | string, dropdownId: AreasField) => {
      clearErrors(dropdownId);
      setValue(dropdownId, value);
    },
    [clearErrors, setValue],
  );

  const handleAddArea = async () => {
    const value = getValues(AreasField.Location);

    if (!value) {
      return;
    }

    const updatedState = requestFormData?.[AreasField.Areas]
      ? [...(requestFormData?.[AreasField.Areas] || []), value]
      : [value];

    setRequestFormData(AreasField.Areas, updatedState);
    setValue(AreasField.Areas, updatedState[0]);
    clearErrors("init");

    await trigger(AreasField.Areas);
  };

  const handleRemoveArea = (id: string) => {
    const filteredAreas = filter(
      requestFormData?.[AreasField.Areas],
      area => +area?.Id !== +id,
    );

    if (filteredAreas?.length) {
      setRequestFormData(AreasField.Areas, filteredAreas);
      setValue(AreasField.Areas, filteredAreas[0]);
    } else {
      setError("init", { message: "AreasForm2" });

      removeRequestFormData(AreasField.Areas);
      setValue(AreasField.Areas, {});
    }
  };

  const isAddedLocation = find(requestFormData?.[AreasField.Areas], area => {
    return Number(area?.Id) === +getValues(AreasField.Location)?.Id;
  });

  const getVisibleLocations = () =>
    filter(
      areas,
      location =>
        !find(
          requestFormData?.[AreasField.Areas],
          area => area?.Id === location?.Id,
        ),
    );

  // Edit mode
  useEffect(() => {
    if (data) {
      const { Lookups } = data;

      if (areas && !requestFormData?.[AreasField.Areas] && isInitialLoad) {
        const normalizedAreas = map(Lookups?.AreaLookup?.Areas, area => ({
          ...area,
          Value: area?.AreaNameEn,
          Company: {
            ...area.Company,
            Value: area.CompanyNameEn,
          },
        }));

        setRequestFormData(AreasField.Areas, normalizedAreas);
        setValue(AreasField.Areas, normalizedAreas[0]);

        map(normalizedAreas, area => {
          handleDataChange(area, AreasField.Location);
        });

        setIsInitialLoad(false);

        trigger(AreasField.Areas);
      }
    }
  }, [
    areas,
    data,
    handleDataChange,
    isInitialLoad,
    requestFormData,
    setRequestFormData,
    trigger,
    setValue,
  ]);

  return (
    <>
      <Row size={RowSize.Small}>
        <h1 className={styles.title}>Select contract location(s)</h1>

        <div className={styles.addLocation}>
          <ValidationWrapper
            errorMessage={errors[AreasField.Location]?.message as string}
          >
            <Dropdown
              containerClassName={styles.dropdownContainer}
              id={AreasField.Location}
              label={AreasFieldLabel[AreasField.Location]}
              placeholder={
                isLoading
                  ? DEFAULT_LOADING_TEXT
                  : DEFAULT_SELECT_PLACEHOLDER_TEXT
              }
              selectedOption={getValues(AreasField.Location)}
              options={getVisibleLocations()}
              isNeedToHighlightSelectedItem
              required={true}
              onChange={handleDataChange}
              onFocus={() => {
                clearErrors(AreasField.Location);
              }}
            />
          </ValidationWrapper>

          <Button
            className={styles.addAreaBtn}
            variant={ButtonVariant.Primary}
            size={ButtonSize.Small}
            disabled={!getValues(AreasField.Location) || !!isAddedLocation}
            onClick={handleAddArea}
          >
            <Plus className={styles.addAreaBtnIcon} />
            <span>Add Location</span>
          </Button>
        </div>
      </Row>

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>

      <Row size={RowSize.Small}>
        <h1 className={styles.titleLocations}>Selected contract location(s)</h1>
        {requestFormData?.[AreasField.Areas]?.length ? (
          map(requestFormData?.[AreasField.Areas], ({ Id, Value }, index) => {
            return (
              <Fragment key={`area-${index}`}>
                <Row className={styles.panelRow}>
                  <span>{Value}</span>
                  <div className={styles.iconContainer}>
                    <Trash onClick={() => handleRemoveArea(`${Id}`)} />
                  </div>
                </Row>
                {+index <
                  (requestFormData?.[AreasField.Areas] || []).length - 1 && (
                  <Divider></Divider>
                )}
              </Fragment>
            );
          })
        ) : (
          <Row className={clsx(styles.panelRow, styles.panelRowEmpty)}>
            No selected locations
          </Row>
        )}
      </Row>

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>
    </>
  );
};
